import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CraneSelectionPanel = ({ selectedCranes, onAddCrane, onRemoveCrane, maxCranes = 4 }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  const craneManufacturers = [
  { value: '', label: 'All Makes' },
  { value: 'liebherr', label: 'Liebherr' },
  { value: 'grove', label: 'Grove' },
  { value: 'terex', label: 'Terex' },
  { value: 'manitowoc', label: 'Manitowoc' },
  { value: 'link-belt', label: 'Link-Belt' },
  { value: 'tadano', label: 'Tadano' },
  { value: 'kobelco', label: 'Kobelco' },
  { value: 'xcmg', label: 'XCMG' }];


  const mockSearchResults = [
  {
    id: 'crane-1',
    make: 'Liebherr',
    model: 'LTM 1200-5.1',
    year: 2020,
    capacity: '200 tons',
    image: "https://images.unsplash.com/photo-1733067996013-ec5b8eec3f52",
    imageAlt: 'Yellow Liebherr mobile crane with extended boom on construction site',
    hours: 2500,
    price: '$850,000',
    location: 'Texas, USA'
  },
  {
    id: 'crane-2',
    make: 'Grove',
    model: 'GMK5250L',
    year: 2019,
    capacity: '250 tons',
    image: "https://images.unsplash.com/photo-1601826757238-2b248f07e7ee",
    imageAlt: 'White Grove all-terrain crane positioned next to industrial building',
    hours: 3200,
    price: '$920,000',
    location: 'California, USA'
  },
  {
    id: 'crane-3',
    make: 'Terex',
    model: 'AC 160-5',
    year: 2021,
    capacity: '160 tons',
    image: "https://images.unsplash.com/photo-1598066319698-8fd4f620917f",
    imageAlt: 'Red Terex mobile crane with telescopic boom at construction yard',
    hours: 1800,
    price: '$780,000',
    location: 'Florida, USA'
  },
  {
    id: 'crane-4',
    make: 'Manitowoc',
    model: '18000',
    year: 2018,
    capacity: '440 tons',
    image: "https://images.unsplash.com/photo-1678860886480-d0d2a3757597",
    imageAlt: 'Large blue Manitowoc crawler crane with lattice boom on job site',
    hours: 4500,
    price: '$1,200,000',
    location: 'New York, USA'
  }];


  const filteredResults = mockSearchResults?.filter((crane) => {
    const matchesSearch = searchQuery === '' ||
    crane?.make?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    crane?.model?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesMake = selectedMake === '' || crane?.make?.toLowerCase() === selectedMake;
    const isNotSelected = !selectedCranes?.find((selected) => selected?.id === crane?.id);

    return matchesSearch && matchesMake && isNotSelected;
  });

  const handleSearch = () => {
    setShowSearchResults(true);
  };

  const handleAddCrane = (crane) => {
    if (selectedCranes?.length < maxCranes) {
      onAddCrane(crane);
      setShowSearchResults(false);
      setSearchQuery('');
    }
  };

  const canAddMore = selectedCranes?.length < maxCranes;

  return (
    <div className="bg-card border border-border rounded-lg p-6 h-fit">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-text-primary">Select Cranes to Compare</h2>
        <span className="text-sm text-text-secondary">
          {selectedCranes?.length}/{maxCranes} selected
        </span>
      </div>
      {/* Search Interface */}
      {canAddMore &&
      <div className="space-y-4 mb-6">
          <Input
          label="Search Cranes"
          type="search"
          placeholder="Enter make, model, or specifications..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          className="mb-4" />


          <Select
          label="Filter by Make"
          options={craneManufacturers}
          value={selectedMake}
          onChange={setSelectedMake}
          className="mb-4" />


          <Button
          variant="default"
          onClick={handleSearch}
          iconName="Search"
          iconPosition="left"
          fullWidth
          disabled={!searchQuery?.trim()}>

            Search Cranes
          </Button>
        </div>
      }
      {/* Search Results */}
      {showSearchResults && canAddMore &&
      <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-text-primary">Search Results</h3>
            <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSearchResults(false)}
            iconName="X" />

          </div>
          
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {filteredResults?.length > 0 ?
          filteredResults?.map((crane) =>
          <div
            key={crane?.id}
            className="flex items-center space-x-3 p-3 border border-border rounded-md hover:bg-muted transition-smooth cursor-pointer"
            onClick={() => handleAddCrane(crane)}>

                  <div className="w-16 h-12 bg-muted rounded overflow-hidden flex-shrink-0">
                    <img
                src={crane?.image}
                alt={crane?.imageAlt}
                className="w-full h-full object-cover" />

                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-text-primary truncate">
                      {crane?.make} {crane?.model}
                    </h4>
                    <p className="text-sm text-text-secondary">
                      {crane?.year} • {crane?.capacity} • {crane?.hours?.toLocaleString()} hrs
                    </p>
                    <p className="text-sm font-medium text-primary">{crane?.price}</p>
                  </div>
                  <Icon name="Plus" size={16} className="text-text-secondary" />
                </div>
          ) :

          <div className="text-center py-8 text-text-secondary">
                <Icon name="Search" size={32} className="mx-auto mb-2 opacity-50" />
                <p>No cranes found matching your criteria</p>
              </div>
          }
          </div>
        </div>
      }
      {/* Selected Cranes */}
      <div>
        <h3 className="font-medium text-text-primary mb-4">Selected for Comparison</h3>
        
        {selectedCranes?.length > 0 ?
        <div className="space-y-3">
            {selectedCranes?.map((crane, index) =>
          <div
            key={crane?.id}
            className="flex items-center space-x-3 p-3 bg-muted rounded-md">

                <div className="w-12 h-8 bg-background rounded overflow-hidden flex-shrink-0">
                  <img
                src={crane?.image}
                alt={crane?.imageAlt}
                className="w-full h-full object-cover" />

                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-text-primary text-sm truncate">
                    {crane?.make} {crane?.model}
                  </h4>
                  <p className="text-xs text-text-secondary">
                    {crane?.year} • {crane?.capacity}
                  </p>
                </div>
                <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemoveCrane(crane?.id)}
              iconName="X"
              className="text-text-secondary hover:text-destructive" />

              </div>
          )}
          </div> :

        <div className="text-center py-8 text-text-secondary">
            <Icon name="GitCompare" size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No cranes selected</p>
            <p className="text-xs">Search and add cranes to start comparing</p>
          </div>
        }
      </div>
      {!canAddMore &&
      <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-md">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <span className="text-sm text-warning">Maximum {maxCranes} cranes can be compared</span>
          </div>
        </div>
      }
    </div>);

};

export default CraneSelectionPanel;